import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface SessionInfo {
  authenticated: boolean;
  via?: 'sso' | 'password';
  userId?: string | null;
  userName?: string | null;
  userRole?: string | null;
  permissions?: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  /** Auth method: 'sso' (full access) or 'password' (Angebote only) */
  sessionVia: 'sso' | 'password' | null;
  /** Display name (SSO only) */
  userName: string | null;
  /** User role (SSO only) */
  userRole: string | null;
  /** Whether user has full access (SSO) or limited (password) */
  hasInvoiceAccess: boolean;
  login: (password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  // Check server session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        // Clean up any SSO error params from URL
        const params = new URLSearchParams(window.location.search);
        const ssoError = params.get('error');
        if (ssoError) {
          console.warn('[Auth] SSO-Fehler:', ssoError);
          params.delete('error');
          const remaining = params.toString();
          const cleanPath = window.location.pathname + (remaining ? `?${remaining}` : '');
          window.history.replaceState({}, '', cleanPath);
        }

        const res = await fetch('/api/auth/me');
        if (res.ok) {
          const data: SessionInfo = await res.json();
          if (data.authenticated) {
            setSession(data);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('[Auth] Session check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = useCallback(async (password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await res.json();

      if (!res.ok) {
        return { success: false, error: data.error || 'Login fehlgeschlagen' };
      }

      // Re-fetch session info
      const meRes = await fetch('/api/auth/me');
      if (meRes.ok) {
        const sessionData: SessionInfo = await meRes.json();
        setSession(sessionData);
        setIsAuthenticated(true);
      }

      return { success: true };
    } catch {
      return { success: false, error: 'Verbindungsfehler' };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch {
      // Ignore logout errors
    }
    setSession(null);
    setIsAuthenticated(false);
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    sessionVia: session?.via ?? null,
    userName: session?.userName ?? null,
    userRole: session?.userRole ?? null,
    hasInvoiceAccess: session?.via === 'sso',
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
